/**
 * Admin → FAQ Manager.
 *
 * Categories and questions are stored as `faq` blocks on the existing `faq`
 * CMS page (one block per category, `heading` = category name). No new schema
 * and no second FAQ system: the storefront /faq page and the AI chat knowledge
 * source both read the same blocks.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { adminCmsPageQuery } from "@/lib/cms/queries";
import { saveCmsPageFn } from "@/lib/cms/cms.functions";
import { newBlockId, type CmsBlock, type CmsFaqBlock, type CmsPage } from "@/lib/admin/cms/store";

export const Route = createFileRoute("/admin/faq")({
  component: () => (
    <RequirePermission perm="cms.read">
      <FaqManager />
    </RequirePermission>
  ),
});

type Category = { id: string; heading: string; demo?: boolean; items: { question: string; answer: string }[] };

function toCategories(blocks: CmsBlock[]): Category[] {
  return blocks
    .filter((b): b is CmsFaqBlock => b.kind === "faq")
    .map((b) => ({
      id: b.id,
      heading: b.heading ?? "Untitled category",
      demo: b.demo,
      items: (b.items ?? []).map((i) => ({ question: i.question ?? "", answer: i.answer ?? "" })),
    }));
}

function FaqManager() {
  const qc = useQueryClient();
  const { data: page, isLoading } = useQuery(adminCmsPageQuery("faq"));
  const [cats, setCats] = useState<Category[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (page && !dirty) setCats(toCategories(page.blocks ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalQuestions = cats.reduce((n, c) => n + c.items.length, 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cats;
    return cats
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (i) => i.question.toLowerCase().includes(q) || i.answer.toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.items.length > 0 || c.heading.toLowerCase().includes(q));
  }, [cats, query]);

  function mutate(next: Category[]) {
    setCats(next);
    setDirty(true);
  }

  function updateCat(id: string, patch: Partial<Category>) {
    mutate(cats.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  function updateItem(catId: string, index: number, patch: Partial<{ question: string; answer: string }>) {
    mutate(
      cats.map((c) =>
        c.id === catId
          ? { ...c, items: c.items.map((it, i) => (i === index ? { ...it, ...patch } : it)) }
          : c,
      ),
    );
  }

  async function save() {
    if (!page) return;
    setSaving(true);
    try {
      const others = (page.blocks ?? []).filter((b) => b.kind !== "faq");
      const blocks: CmsBlock[] = [
        ...others,
        ...cats.map<CmsFaqBlock>((c) => ({
          id: c.id,
          kind: "faq",
          heading: c.heading.trim() || "Untitled category",
          ...(c.demo ? { demo: true } : {}),
          items: c.items
            .filter((i) => i.question.trim())
            .map((i) => ({ question: i.question.trim(), answer: i.answer.trim() })),
        })),
      ];
      const saved = (await saveCmsPageFn({
        data: {
          slug: page.slug,
          title: page.title,
          description: page.description,
          seo: page.seo,
          blocks,
        },
      })) as CmsPage;
      setDirty(false);
      setCats(toCategories(saved.blocks ?? []));
      await qc.invalidateQueries({ queryKey: ["cms"] });
      toast.success("FAQ saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <p className="text-sm text-black/60">Loading FAQ…</p>;
  if (!page)
    return <p className="text-sm text-black/60">The FAQ page was not found in CMS pages.</p>;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="FAQ manager"
        subtitle={`${cats.length} categories | ${totalQuestions} questions | powers /faq and the AI fragrance expert`}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 border border-black/10 bg-white px-4 py-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions or answers…"
          className="w-full max-w-sm border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]"
        />
        <div className="flex items-center gap-2">
          {dirty && <StatusPill tone="warn">Unsaved changes</StatusPill>}
          <RequirePermission perm="cms.write" fallback={null}>
            <button
              type="button"
              onClick={() =>
                mutate([
                  ...cats,
                  { id: newBlockId(), heading: "New category", demo: true, items: [] },
                ])
              }
              className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-black/50"
            >
              Add category
            </button>
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={() => void save()}
              className="bg-[#C6A15B] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-black disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </RequirePermission>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {filtered.map((cat) => {
          const expanded = open === cat.id || Boolean(query.trim());
          return (
            <section key={cat.id} className="border border-black/10 bg-white">
              <header className="flex flex-wrap items-center gap-3 border-b border-black/10 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setOpen(expanded && !query.trim() ? null : cat.id)}
                  className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/50 hover:text-[#C6A15B]"
                  aria-expanded={expanded}
                >
                  {expanded ? "▾" : "▸"}
                </button>
                <input
                  value={cat.heading}
                  onChange={(e) => updateCat(cat.id, { heading: e.target.value })}
                  className="flex-1 min-w-[200px] border-b border-transparent bg-transparent text-sm font-semibold text-black outline-none focus:border-[#C6A15B]"
                />
                {cat.demo && <StatusPill tone="warn">Demo</StatusPill>}
                <span className="text-[11px] uppercase tracking-[0.2em] text-black/50">
                  {cat.items.length} questions
                </span>
                <RequirePermission perm="cms.write" fallback={null}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(`Delete category "${cat.heading}" and its questions?`)) return;
                      mutate(cats.filter((c) => c.id !== cat.id));
                    }}
                    className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/50 hover:text-red-600"
                  >
                    Delete
                  </button>
                </RequirePermission>
              </header>

              {expanded && (
                <div className="flex flex-col gap-4 px-4 py-4">
                  {cat.items.length === 0 && (
                    <p className="text-sm text-black/50">No questions in this category yet.</p>
                  )}
                  {cat.items.map((item, index) => {
                    const realIndex = cats
                      .find((c) => c.id === cat.id)!
                      .items.findIndex((i) => i.question === item.question && i.answer === item.answer);
                    const idx = realIndex >= 0 ? realIndex : index;
                    return (
                      <div key={`${cat.id}-${idx}`} className="border border-black/10 p-3">
                        <div className="flex items-start gap-3">
                          <span className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C6A15B]">
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                          <div className="flex-1 flex flex-col gap-2">
                            <input
                              value={item.question}
                              onChange={(e) => updateItem(cat.id, idx, { question: e.target.value })}
                              placeholder="Question"
                              className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]"
                            />
                            <textarea
                              value={item.answer}
                              onChange={(e) => updateItem(cat.id, idx, { answer: e.target.value })}
                              placeholder="Answer"
                              rows={3}
                              className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]"
                            />
                          </div>
                          <RequirePermission perm="cms.write" fallback={null}>
                            <button
                              type="button"
                              onClick={() =>
                                mutate(
                                  cats.map((c) =>
                                    c.id === cat.id
                                      ? { ...c, items: c.items.filter((_, i) => i !== idx) }
                                      : c,
                                  ),
                                )
                              }
                              className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/50 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </RequirePermission>
                        </div>
                      </div>
                    );
                  })}
                  <RequirePermission perm="cms.write" fallback={null}>
                    <button
                      type="button"
                      onClick={() =>
                        mutate(
                          cats.map((c) =>
                            c.id === cat.id
                              ? { ...c, items: [...c.items, { question: "", answer: "" }] }
                              : c,
                          ),
                        )
                      }
                      className="self-start border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-black/50"
                    >
                      Add question
                    </button>
                  </RequirePermission>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
